/**
 * AccountTimeline.tsx — the account page's ONE timeline (Account detail v3
 * AD1, design note §45; spec §5): All / Activity / Changes over the activities
 * logged against the account's deals and contacts and the account's own field
 * history, newest first, each filter with its count.
 *
 * The Activity count is the server's (SalesAccountSummary.activityCount, every
 * row); the list beside it is the 360's newest 100 and says so when the count
 * is larger. Changes come from the change-history strip's own reads
 * (useAuditFeed) and render as its rows. A failed history read is said, never
 * shown as "no changes".
 *
 * The empty-activity banner (spec §5) shows while the account has no touch,
 * with "Log the first one" opening the ONE Log activity sheet — absent for a
 * caller without records access (useLogActivity is null then).
 */
import { useState } from 'react'
import { Phone, Mail, Users, FileText, CheckSquare } from 'lucide-react'
import type { SalesActivity as WireActivity, SalesAccountSummary } from '../../../../api/types'
import { relAgo, fmtDate } from '../../api'
import { useAuditFeed } from './auditFeed'
import { AuditRowItem } from './SalesAuditStrip'
import { emptyActivityBanner } from './accountDetailView'

const TYPE_ICON: Record<string, React.ReactNode> = {
  call: <Phone size={13} />, email: <Mail size={13} />, meeting: <Users size={13} />,
  note: <FileText size={13} />, task: <CheckSquare size={13} />,
}

type Filter = 'all' | 'activity' | 'change'

/** An activity's place in time: when it was done, else when it was logged. */
const activityAt = (a: WireActivity) => a.completedAt ?? a.createdAt

export function AccountTimeline({ accountId, activities, summary, ageDays, onLogFirst }: {
  accountId: string
  activities: readonly WireActivity[]
  /** Null from an api older than AD1: no counts beyond what the list holds, no banner. */
  summary: SalesAccountSummary | null
  ageDays: number | null
  /** Opens the Log activity sheet; absent = the caller cannot log. */
  onLogFirst?: () => void
}) {
  const [filter, setFilter] = useState<Filter>('all')
  const feed = useAuditFeed('accounts', accountId)
  const changeCount = feed.failed ? 0 : feed.rows.length
  const activityCount = summary ? summary.activityCount : activities.length

  type Entry = { key: string; at: string; node: React.ReactNode }
  const entries: Entry[] = []
  if (filter !== 'change') {
    for (const a of activities) {
      entries.push({
        key: `a:${a.id}`, at: activityAt(a), node: (
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-surface-panel border border-edge-row flex items-center justify-center text-ink-muted flex-shrink-0">
              {TYPE_ICON[a.activityType] ?? <FileText size={13} />}
            </div>
            <div className="min-w-0">
              <div className="text-13 font-semibold text-ink-primary">{a.subject}</div>
              {a.body && <div className="text-12 text-ink-secondary mt-1">{a.body}</div>}
              <div className="text-11 text-ink-muted mt-1">{relAgo(activityAt(a))} · {fmtDate(activityAt(a))}</div>
            </div>
          </div>
        ),
      })
    }
  }
  if (filter !== 'activity' && !feed.loading && !feed.failed) {
    for (const r of feed.rows) {
      entries.push({ key: `c:${r.key}`, at: r.at, node: <AuditRowItem row={r} joins={feed.joins} noun="Account" /> })
    }
  }
  entries.sort((x, y) => Date.parse(y.at) - Date.parse(x.at))

  const banner = summary ? emptyActivityBanner(summary, ageDays, changeCount) : null
  const counts: Record<Filter, number> = { all: activityCount + changeCount, activity: activityCount, change: changeCount }
  const FILTERS: { value: Filter; label: string }[] = [
    { value: 'all', label: 'All' }, { value: 'activity', label: 'Activity' }, { value: 'change', label: 'Changes' },
  ]

  return (
    <div className="k-card">
      <div className="k-card-head">
        <div>
          <div className="k-card-title">Timeline</div>
          <div className="k-card-sub">Activity on this account's deals and contacts, and changes to the account</div>
        </div>
      </div>
      <div className="px-5 pt-3 flex items-center gap-2 flex-wrap" role="group" aria-label="Timeline filter">
        {FILTERS.map(f => (
          <button key={f.value} type="button" aria-pressed={filter === f.value}
            className={filter === f.value ? 'k-btn' : 'k-btn-secondary'} onClick={() => setFilter(f.value)}>
            {f.label} <span className="text-ink-muted">{counts[f.value]}</span>
          </button>
        ))}
      </div>
      <div className="p-5 flex flex-col gap-4">
        {banner && (
          <div className="rounded-control border border-edge-row bg-surface-panel px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
            <span className="text-13 text-ink-secondary">{banner}</span>
            {onLogFirst && <button type="button" className="k-btn-secondary" onClick={onLogFirst}>Log the first one</button>}
          </div>
        )}
        {filter !== 'activity' && feed.loading && <div className="text-13 text-ink-muted">Loading history…</div>}
        {filter !== 'activity' && !feed.loading && feed.failed && <div className="text-13 text-ink-muted">Change history unavailable.</div>}
        {entries.length === 0 && !(filter !== 'activity' && (feed.loading || feed.failed)) && (
          <div className="text-13 text-ink-muted">
            {filter === 'change' ? 'No changes recorded.' : filter === 'activity' ? 'No activity logged yet.' : 'Nothing on this account yet.'}
          </div>
        )}
        {entries.map(e => <div key={e.key}>{e.node}</div>)}
        {filter !== 'change' && activityCount > activities.length && (
          <div className="text-12 text-ink-muted">Showing the latest {activities.length} of {activityCount} activities.</div>
        )}
      </div>
    </div>
  )
}
